import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Eye, EyeOff, AlertCircle } from "lucide-react";
import { useAuth, redirectByRole } from "../lib/auth";

const DEMO_USERS = [
  { label: "Super Admin", email: "admin@freddycmpute.com", password: "admin123" },
  { label: "Executive", email: "exec@freddycmpute.com", password: "exec123" },
  { label: "Staff", email: "staff@freddycmpute.com", password: "staff123" },
  { label: "New (no plan)", email: "new@freddycmpute.com", password: "new123" },
];

export default function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      const user = await login(email, password);
      const dest = location.state?.from || redirectByRole(user.role);
      navigate(dest, { replace: true });
    } catch (err) {
      console.error("Login error", err);
      setError(err.response?.data?.detail || "Login failed. Check your credentials.");
    } finally {
      setBusy(false);
    }
  };

  const fillDemo = (u) => {
    setEmail(u.email);
    setPassword(u.password);
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-fc-navy text-white" data-testid="login-page">
      {/* LEFT — brand panel */}
      <div className="relative hidden lg:flex flex-col bg-[#060A24] p-12 overflow-hidden">
        <div className="absolute inset-0 fc-grid-pattern opacity-30 pointer-events-none" />
        <Link to="/" className="relative flex items-center gap-3 z-10">
          <img src="/brand/freddy-symbol.webp" alt="Freddy" className="h-10 w-auto" />
          <span className="text-white font-black tracking-[0.18em] text-xs">
            FREDDY COMPUTE ACADEMY
          </span>
        </Link>

        <div className="relative z-10 m-auto max-w-md w-full">
          <img
            src="/brand/freddy-seal.webp"
            alt="Freddy seal"
            className="w-32 h-32 object-contain mb-10 opacity-90"
          />
          <p className="fc-overline mb-4">WHERE AUTOMOTIVE DEALERS LEARN TO BUILD WITH AI</p>
          <h1 className="fc-h-section text-4xl xl:text-5xl leading-[1.05] mb-8">
            Welcome back to the <span className="text-fc-red">academy</span>.
          </h1>
          <p className="text-white/65 leading-relaxed text-sm">
            Resume your program. Pick up where you left off. Push another result into your store this week.
          </p>
        </div>

        <p className="relative z-10 text-xs text-white/35 tracking-wider">© 2026 Freddy Compute</p>
      </div>

      {/* RIGHT — form */}
      <div className="flex flex-col p-6 sm:p-12">
        <div className="flex items-center justify-between lg:hidden mb-10">
          <Link to="/" className="flex items-center gap-2">
            <img src="/brand/freddy-symbol.webp" alt="Freddy" className="h-8 w-auto" />
            <span className="text-white font-black tracking-[0.18em] text-[10px]">FREDDY COMPUTE</span>
          </Link>
        </div>

        <div className="m-auto w-full max-w-md">
          <h2 className="fc-h-section text-3xl sm:text-4xl mb-3">Login</h2>
          <p className="text-white/60 mb-8 text-sm">Welcome back. Sign in to continue your program.</p>

          {error && (
            <div className="flex items-center gap-2 bg-fc-red/10 border border-fc-red/40 text-fc-red text-sm p-3 mb-5" data-testid="login-error">
              <AlertCircle size={16} /> {error}
            </div>
          )}

          <form onSubmit={onSubmit} className="space-y-5" data-testid="login-form">
            <label className="block">
              <span className="text-[10px] tracking-[0.22em] uppercase font-bold text-white/70 block mb-2">Email</span>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full bg-[#060A24] border border-white/10 focus:border-fc-red text-white px-4 py-3 outline-none transition-colors"
                placeholder="you@dealership.com"
                data-testid="login-email-input"
              />
            </label>
            <label className="block">
              <span className="text-[10px] tracking-[0.22em] uppercase font-bold text-white/70 block mb-2">Password</span>
              <div className="relative">
                <input
                  type={show ? "text" : "password"}
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full bg-[#060A24] border border-white/10 focus:border-fc-red text-white px-4 py-3 pr-12 outline-none transition-colors"
                  placeholder="••••••••"
                  data-testid="login-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShow((v) => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-white"
                  aria-label={show ? "Hide password" : "Show password"}
                >
                  {show ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </label>

            <div className="flex items-center justify-between text-xs">
              <Link to="/forgot-password" className="text-white/60 hover:text-fc-red font-semibold" data-testid="forgot-password-link">
                Forgot password?
              </Link>
              <Link to="/register" className="text-white/60 hover:text-fc-red font-semibold" data-testid="register-link">
                Don't have an account? Register
              </Link>
            </div>

            <button
              type="submit"
              disabled={busy}
              className="btn-fc-red w-full justify-center"
              data-testid="login-submit-btn"
            >
              {busy ? "Signing in…" : "Sign In"}
            </button>
          </form>

          {/* Demo creds */}
          <div className="mt-10 bg-[#060A24] border border-white/10 p-5" data-testid="demo-credentials-card">
            <p className="fc-overline mb-3 text-[10px]">DEMO CREDENTIALS (CLICK TO FILL)</p>
            <ul className="space-y-2 text-xs">
              {DEMO_USERS.map((u) => (
                <li key={u.email} className="flex items-center justify-between gap-2">
                  <span className="text-white/80">
                    <strong>{u.label}:</strong> {u.email} / {u.password}
                  </span>
                  <button
                    type="button"
                    onClick={() => fillDemo(u)}
                    className="text-fc-red font-bold text-[10px] tracking-[0.18em] hover:underline"
                    data-testid={`demo-fill-${u.email}`}
                  >
                    USE
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}
